import { useQuery } from "@apollo/client";
import useCapabilities, { useCanViewCommitteesBool, useHasCapabilityBool } from "@/hooks/useCapabilities";
import { DOMAIN, PERMISSION } from "@/providers/AuthorizationProvider";
import { useFlags } from "@/hooks/useFeatureFlags";
import { getSectionOperationsVisibility } from "../_utils";
import { SECTION_NAV_QUERY } from "../queries";
import { useSectionCommitteeManagementGate } from "./useSectionCommitteeManagementEnabled";

export function useSectionOperationsAccess(sectionId?: string) {
  const { loaded: capabilitiesLoaded } = useCapabilities();
  const flags = useFlags();
  const canViewFacilities = useHasCapabilityBool({
    domain: DOMAIN.FACILITIES,
    permission: PERMISSION.viewFacilities
  });
  const canViewCommittees = useCanViewCommitteesBool();
  const { enabled: sectionCommitteeManagementEnabled, flagsLoaded } = useSectionCommitteeManagementGate();
  const governanceEnabled = flags["backoffice-section-governance"] === true;
  const notificationEmailsEnabled = flags["backoffice-section-notification-emails"] === true;
  const skipSectionOfficeQuery = !sectionId || (!canViewFacilities && !notificationEmailsEnabled);
  const { data, loading, error } = useQuery(SECTION_NAV_QUERY, {
    variables: {
      sectionId: sectionId ?? "",
      includeNotificationPermissions: notificationEmailsEnabled
    },
    skip: skipSectionOfficeQuery,
    fetchPolicy: "cache-first"
  });

  const sectionOfficeFacilityId = data?.section?.sectionOffice?.id ?? null;
  const sectionNavError = skipSectionOfficeQuery ? null : (error ?? null);
  const visibility = getSectionOperationsVisibility({
    sectionOfficeFacilityId,
    canViewFacilities,
    canViewCommittees,
    sectionCommitteeManagementEnabled,
    canManageNotificationEmails: data?.section?.permissions?.manageNotificationEmails === true,
    notificationEmailsEnabled
  });
  // Keep facility Operations tabs reachable when SECTION_NAV fails so pages can show an error
  // instead of looking like "no office" / ejecting the user to Jobs.
  const showSectionOfficeFacilityTabs =
    visibility.showSectionOfficeFacilityTabs || Boolean(canViewFacilities && sectionNavError);
  const showNotifications = visibility.showNotifications;
  const showOperations = visibility.showCommittees || showSectionOfficeFacilityTabs || showNotifications;

  return {
    ready: capabilitiesLoaded && flagsLoaded && (skipSectionOfficeQuery || !loading || data != null || error != null),
    sectionOfficeFacilityId,
    sectionNavError,
    canViewFacilities,
    showCommittees: visibility.showCommittees,
    showSectionOfficeFacilityTabs,
    governanceEnabled,
    showGovernance: showSectionOfficeFacilityTabs && governanceEnabled,
    showOperations,
    showNotifications
  };
}
